import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Autocomplete, Chip, IconButton, TextField } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';

/**
 * Shared tag-input field used by both the dataset Create dialog (admin)
 * and the Inspector edit form (non-admin owner/admin). Replaces a plain
 * comma-separated TextField.
 *
 * Built on MUI's <Autocomplete multiple freeSolo>, which gives us chips,
 * delete affordance, suggestion popup, keyboard navigation and ARIA for
 * free. We add three things on top:
 *   1. Comma is a commit separator (not just Enter / blur / select),
 *      so users pasting "foo, bar, baz" from the old free-form field
 *      get three chips, not one weird chip.
 *   2. Dedup is case-insensitive but the FIRST casing wins — typing
 *      "Telecom" then "telecom" yields one chip "Telecom". Storage
 *      keeps the user's casing; only the duplicate test is folded.
 *   3. Suggestions exclude already-committed tags on the current value,
 *      so the dropdown never offers a tag the user already has.
 *
 * The component is presentational: parent owns the array and feeds in
 * suggestions (typically via useTagUniverse). That keeps it trivially
 * testable and avoids coupling either the dialog or the inspector to
 * the tag-universe hook directly.
 */

// Empty-string / whitespace-only check, used by normalise() to drop
// candidates the user clearly didn't mean to commit.
const isBlank = (s) => !s || !s.trim();

// Case-insensitive membership check against an array of strings.
const containsCI = (arr, candidate) => {
    const k = candidate.toLowerCase();
    return arr.some(t => t.toLowerCase() === k);
};

export default function TagInput({
    value,
    onChange,
    suggestions,
    label,
    helperText,
    error,
    disabled,
    placeholder,
    id,
    fullWidth,
    margin,
    size,
    ...textFieldProps
}) {
    // Live input text. We control it so we can split on comma mid-typing
    // — Autocomplete's default uncontrolled input would only commit on
    // Enter/blur, which means a paste of "a, b, c" stays as one chip.
    const [inputValue, setInputValue] = useState('');

    // Filter suggestions: never offer something the user already has.
    // Case-insensitive so "Telecom" hides when "telecom" is committed.
    const filteredSuggestions = suggestions.filter(s => !containsCI(value, s));

    /** Normalise a candidate array into the canonical shape: trim,
     *  drop empties, and dedup case-insensitively while preserving the
     *  first casing. Used both for additions (free-solo / suggestion
     *  pick) and for the Autocomplete-supplied array on removal — in
     *  the latter case the input is already canonical, so this is a
     *  cheap no-op. */
    const normalise = (candidates) => {
        const out = [];
        for (const raw of candidates) {
            if (typeof raw !== 'string') continue;
            const trimmed = raw.trim();
            if (isBlank(trimmed)) continue;
            if (containsCI(out, trimmed)) continue; // duplicate, drop
            out.push(trimmed);
        }
        return out;
    };

    const handleChange = (_event, newValue, reason) => {
        // Autocomplete fires with the full new array, plus a `reason`
        // describing the user action. We canonicalise the array (trim,
        // dedup) before forwarding it. For the removal path the input
        // is already canonical, so normalise is a pass-through.
        if (reason === 'clear') {
            onChange([]);
            setInputValue('');
            return;
        }
        const next = normalise(newValue);
        // Avoid a no-op render (e.g. Enter on a duplicate).
        if (next.length !== value.length || next.some((t, i) => t !== value[i])) {
            onChange(next);
        }
        setInputValue('');
    };

    const handleInputChange = (_event, newInput, reason) => {
        // 'reset' fires after a chip is added — Autocomplete clears the
        // input itself; respect that and don't fight it.
        if (reason === 'reset') {
            setInputValue('');
            return;
        }
        // Comma as a separator: the moment a comma appears in the
        // input, split everything before it into chips and keep the
        // tail (text after the last comma) as the new live input.
        if (newInput.includes(',')) {
            const parts = newInput.split(',');
            const tail = parts.pop(); // may be '' if user typed "foo,"
            // Append parts to the existing value, going through
            // normalise to dedup against current chips too.
            const merged = normalise([...value, ...parts]);
            if (merged.length !== value.length) onChange(merged);
            setInputValue(tail.trimStart());
            return;
        }
        setInputValue(newInput);
    };

    return (
        <Autocomplete
            multiple
            freeSolo
            autoSelect           // Tab/blur with text → commits as a chip
            handleHomeEndKeys
            disabled={disabled}
            options={filteredSuggestions}
            value={value}
            inputValue={inputValue}
            onChange={handleChange}
            onInputChange={handleInputChange}
            // Render committed tags as chips. We override MUI's default
            // tag renderer to control the delete affordance: the bundled
            // one is a bare SVG without an accessible name. Render the
            // delete as a real <IconButton> so screen readers announce
            // "Remove tag telecom", and so click semantics work the same
            // way they do everywhere else in the app. We bypass Chip's
            // own onDelete plumbing — the IconButton owns the click —
            // because Chip's cloneElement-based deleteIcon contract is
            // fragile across MUI versions.
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((tag, index) => {
                    const { key, onDelete, ...chipProps } = getTagProps({ index });
                    return (
                        <Chip
                            key={key}
                            variant="outlined"
                            size="small"
                            label={tag}
                            onDelete={onDelete}
                            deleteIcon={
                                <IconButton
                                    aria-label={`Remove tag ${tag}`}
                                    size="small"
                                    // Stop the click from bubbling to
                                    // Chip's root (which would otherwise
                                    // also try to handle it as a chip
                                    // click — we only want delete).
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(e);
                                    }}
                                >
                                    <CancelIcon fontSize="small" />
                                </IconButton>
                            }
                            {...chipProps}
                        />
                    );
                })
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    id={id}
                    label={label}
                    placeholder={placeholder}
                    error={error}
                    helperText={helperText}
                    fullWidth={fullWidth}
                    margin={margin}
                    size={size}
                    {...textFieldProps}
                />
            )}
        />
    );
}

TagInput.propTypes = {
    value: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
    suggestions: PropTypes.arrayOf(PropTypes.string),
    label: PropTypes.node,
    helperText: PropTypes.node,
    error: PropTypes.bool,
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    id: PropTypes.string,
    fullWidth: PropTypes.bool,
    margin: PropTypes.string,
    size: PropTypes.string
};

TagInput.defaultProps = {
    suggestions: [],
    fullWidth: true,
    margin: 'dense',
    size: 'small'
};
